import React, { useState } from "react";
import { FiAlertTriangle, FiActivity, FiCheckCircle } from "react-icons/fi";
import { MiniBar, SeverityBadge, severityColor } from "./waterHelpers";

const STATUS_STYLE = {
  "Under Investigation": { bg: "#fef3c7", color: "#d97706" },
  "Alert Sent":          { bg: "#fee2e2", color: "#dc2626" },
  "Resolved":            { bg: "#d1fae5", color: "#059669" },
  "Auto-stopped":        { bg: "#d1fae5", color: "#059669" },
  "Monitoring":          { bg: "#dbeafe", color: "#2563eb" },
};

const WmLeakagePanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const lk = d.leakage;
  const sel = selected ? lk.anomalies.find(a => a.id === selected) : null;

  return (
    <div className="wm-leakage">

      {/* ── Risk Summary ── */}
      <div className="wl-summary-row">
        <div className="wl-sum-card" style={{ borderColor: "#ef4444" }}>
          <FiAlertTriangle size={20} color="#ef4444" />
          <div className="wl-sum-num" style={{ color: "#ef4444" }}>{lk.anomalies.filter(a => a.severity === "Critical").length}</div>
          <div className="wl-sum-lbl">Critical Anomalies</div>
        </div>
        <div className="wl-sum-card" style={{ borderColor: "#f59e0b" }}>
          <FiActivity size={20} color="#f59e0b" />
          <div className="wl-sum-num" style={{ color: "#f59e0b" }}>{lk.totalSuspectedLoss} L</div>
          <div className="wl-sum-lbl">Suspected Loss/Day</div>
        </div>
        <div className="wl-sum-card" style={{ borderColor: "#ec4899" }}>
          <FiActivity size={20} color="#ec4899" />
          <div className="wl-sum-num" style={{ color: "#ec4899" }}>₹{lk.estimatedCostLoss}</div>
          <div className="wl-sum-lbl">Est. Daily Cost Loss</div>
        </div>
        <div className="wl-sum-card" style={{ borderColor: "#6366f1" }}>
          <FiActivity size={20} color="#6366f1" />
          <div className="wl-sum-num" style={{ color: "#6366f1" }}>{lk.nightFlowActual} L/hr</div>
          <div className="wl-sum-lbl">Night Flow (actual)</div>
        </div>

        {/* Night flow comparison */}
        <div className="wl-nightflow-card">
          <div className="wl-nf-title">Night Flow Analysis (1–4 AM)</div>
          <div className="wl-nf-row">
            <span>Baseline (normal)</span>
            <MiniBar value={lk.nightFlowBaseline} max={lk.nightFlowActual} color="#10b981" height={8} />
            <span>{lk.nightFlowBaseline} L/hr</span>
          </div>
          <div className="wl-nf-row">
            <span>Actual (detected)</span>
            <MiniBar value={lk.nightFlowActual} max={lk.nightFlowActual} color="#ef4444" height={8} />
            <span style={{ color: "#ef4444", fontWeight: 600 }}>{lk.nightFlowActual} L/hr</span>
          </div>
          <div className="wl-nf-diff">
            ↑ {Math.round((lk.nightFlowActual / lk.nightFlowBaseline - 1) * 100)}% above baseline — possible hidden leak
          </div>
        </div>
      </div>

      <div className="wl-layout">

        {/* ── Anomaly List ── */}
        <div className="wl-list">
          {lk.anomalies.map(a => {
            const ss = STATUS_STYLE[a.status] || { bg: "#f1f5f9", color: "#475569" };
            return (
              <div
                key={a.id}
                className={`wl-anomaly-card ${selected === a.id ? "wl-anomaly-active" : ""}`}
                onClick={() => setSelected(selected === a.id ? null : a.id)}
              >
                <div className="wl-a-header">
                  <div className="wl-a-left">
                    <SeverityBadge severity={a.severity} />
                    <span className="wl-a-type">{a.type}</span>
                  </div>
                  <span className="wl-a-status" style={{ background: ss.bg, color: ss.color }}>{a.status}</span>
                </div>
                <div className="wl-a-location">{a.location}</div>
                <div className="wl-a-meta">
                  <span className="wl-a-prob">
                    <div className="wl-prob-bar-wrap">
                      <div className="wl-prob-bar" style={{ width: `${a.probability}%`, background: severityColor(a.severity) }} />
                    </div>
                    {a.probability}% probability
                  </span>
                  <span className="wl-a-flow">{a.flow}</span>
                </div>
                <div className="wl-a-time">Detected: {a.detectedAt} · Duration: {a.duration}</div>
              </div>
            );
          })}
        </div>

        {/* ── Detail Panel ── */}
        <div className="wl-detail">
          {sel ? (
            <>
              <div className="wl-detail-title">{sel.type}</div>
              <div style={{ display: "flex", gap: "0.75rem", margin: "0.75rem 0", alignItems: "center" }}>
                <SeverityBadge severity={sel.severity} />
                <span className="wl-a-status" style={{ ...(STATUS_STYLE[sel.status] || {}), padding: "0.15rem 0.55rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 600 }}>{sel.status}</span>
              </div>

              <div className="wl-detail-grid">
                {[
                  ["Location",  sel.location],
                  ["Flow",      sel.flow],
                  ["Detected",  sel.detectedAt],
                  ["Duration",  sel.duration],
                ].map(([label, val]) => (
                  <div key={label} className="wl-dg-item">
                    <span className="wl-dg-label">{label}</span>
                    <span className="wl-dg-val">{val}</span>
                  </div>
                ))}
              </div>

              <div className="wl-probability">
                <div className="wl-prob-title">Leakage Probability</div>
                <div className="wl-prob-num" style={{ color: severityColor(sel.severity) }}>{sel.probability}%</div>
                <MiniBar value={sel.probability} color={severityColor(sel.severity)} height={10} />
              </div>

              <div className="wl-recommendation">
                <div className="wl-rec-title">
                  {sel.status === "Resolved" || sel.status === "Auto-stopped"
                    ? <><FiCheckCircle size={14} color="#10b981" /> Resolution</>
                    : <><FiAlertTriangle size={14} color="#f59e0b" /> Recommended Action</>}
                </div>
                <div className="wl-rec-body">{sel.recommendation}</div>
              </div>
            </>
          ) : (
            <div className="wl-empty">
              <FiActivity size={40} color="#cbd5e1" />
              <p>Select an anomaly to view details and recommendations</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default WmLeakagePanel;
